import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import ButtonIcon from '../ui/ButtonIcon';



export const useSelectedSnapDisplay = (emblaApi) => {
  const [selectedSnap, setSelectedSnap] = useState(0)
  const [snapCount, setSnapCount] = useState(0)

  const updateScrollSnapState = useCallback((emblaApi) => {
    setSnapCount(emblaApi.scrollSnapList().length)
    setSelectedSnap(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    updateScrollSnapState(emblaApi)
    emblaApi.on('select', updateScrollSnapState)
    emblaApi.on('reInit', updateScrollSnapState)
  }, [emblaApi, updateScrollSnapState])

  return {
    selectedSnap,
    snapCount
  }
}



const HomeWWD = ({ items, title, text, btn_text }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start' });
  const { selectedSnap, snapCount } = useSelectedSnapDisplay(emblaApi);

  const itemsRefs = useRef([]);
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const textRef = useRef(null);



    const scrollPrev = useCallback(() => {
      if (emblaApi) emblaApi.scrollPrev()
    }, [emblaApi])
  
    const scrollNext = useCallback(() => {
      if (emblaApi) {
        emblaApi.scrollNext()}
    }, [emblaApi, snapCount, selectedSnap])
  
  

  const containerRef = useRef(null)

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add('(min-width: 1280px)', () => {
      if (titleRef.current) {
        gsap.fromTo(
          titleRef.current,
          { opacity: 0 },
          {
            opacity: 1,
            duration: 1,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 70%',
            },
          }
        );
      }

      if (textRef.current) {
        gsap.fromTo(
          textRef.current,
          { opacity: 0 },
          {
            opacity: 1,
            duration: 1,
            delay: 0.2,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 70%',
            },
          }
        );
      }

      
      gsap.fromTo(containerRef.current, {
        y: '13rem'
      },
          {
        y: 0,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom", 
          end: "top 60%",  
          scrub: 0.1,  
        },
      });

      const offsets = [20, 10, 15, 25];

      itemsRefs.current.forEach((item, index) => {
        const initialY = offsets[index] ?? 0;

        gsap.fromTo(
          item,
          { y: `${initialY}rem` },
          {
            y: 0,
            duration: 1,
            delay: index * 0.1,
            scrollTrigger: {
            trigger: item.parentElement,
            start: "top 100%",
            end: "top 40%",
              scrub: 0.5,
            },
          }
        );

        const imageElement = item.querySelector('img');
        if (imageElement) {
          gsap.fromTo(
            imageElement,
            { y: '-4rem' },
            {
              y: '4rem',
              ease: 'none',
              scrollTrigger: {
                trigger: item.parentElement,
                start: "top bottom",
                end: "bottom start",
                scrub: 0,
              },
            }
          );
        }
      });
    });

    return () => mm.revert();
  },{scope: sectionRef, dependencies: items});

  return (
    <section
      ref={sectionRef}
      className="container-max w-full py-[4rem] md:py-[6rem] lg:py-[8rem] xl:py-[10rem] "
    >
      <div ref={containerRef} className='flex flex-col w-full'>
        <div className="mb-[2.4rem] md:mb-[4rem] lg:mb-[8rem] text-left container-padding flex-col container-grid">
          <h2
            ref={titleRef}
            className="w-full md:mb-0 lg:text-right md:col-span-2 mb-[2rem] lg:col-span-3 lg:col-start-3"
          >
            {title}
          </h2>
          <p
            ref={textRef}
            className="lg:flex lg:items-center text-1 md:col-span-2 md:col-start-4 lg:col-span-4 pr-[16%] lg:col-start-7"
          >
            {text}
          </p>
        </div>

        {/* Mobile carousel */}
        <div
          className="embla lg:hidden mb-[4rem] px-[2rem] md:px-[2.4rem]"
          ref={emblaRef}
        >
          <div className="embla__container gap-[1.2rem] md:gap-[1.6rem]">
            {items.map((item, index) => (
              <div
                key={index}
                className="embla__slide flex flex-col w-[calc(100%-4rem)] md:flex-row items-stretch flex-[0_0_31.6rem] md:flex-[0_0_59.6rem]"
              >
                <div className="relative aspect-[1.44/1] md:min-w-[28.9rem] md:aspect-auto w-full">
                  <Image
                    src={item.image.url}
                    alt={item.image?.alt}
                    fill
                    sizes="500px"
                    className="w-auto object-cover"
                  />
                </div>
                <div className="flex-1 flex flex-col items-start justify-between md:flex-initial bg-dusty-pink-light p-[2rem]">
                  <div>
                    <h3 className="heading-4 mb-[1.6rem]">{item.name ?? item.title}</h3>
                    <div className="w-full flex flex-col gap-[1.6rem] md:mb-[15.2rem] mb-[4.8rem]">
                      {item.text.map((t, textIndex) => (
                        <p key={`${index}-text-${textIndex}`} className="text-1">
                          {t.text}
                        </p>
                      ))}
                    </div>
                  </div>

                  <a href={item.link} className="text-link">
                    {btn_text}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='lg:hidden items-center flex  justify-center mb-[2rem]  w-full'>
          <ButtonIcon onClick={scrollPrev} isDisabled={selectedSnap === 0} ><svg className='rotate-180' width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 7H12M12 7L6 1M12 7L6 13" stroke="CurrentColor"/>
                        </svg></ButtonIcon>
              <div className="heading-4 embla__selected-snap-display w-[12.2rem]  flex items-center justify-center">
                {selectedSnap + 1}/{snapCount}
              </div>
          <ButtonIcon onClick={scrollNext} isDisabled={selectedSnap + 1 >= snapCount} ><svg className='' width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 7H12M12 7L6 1M12 7L6 13" stroke="CurrentColor"/>
          </svg></ButtonIcon>
        </div>

        {/* Desktop cards */}
        <div className="hidden lg:flex container-padding mb-[4rem]">
          <div className="flex w-full gap-[8.3%]">
            {items.map((item, index) => (
              <div
                key={index}
                ref={(el) => {
                  if (el) itemsRefs.current[index] = el;
                }}
                className="relative card group w-full flex flex-col"
              >
                <a href={item.link} aria-label='Перейти к услуге' className="w-full mb-[1.2rem] overflow-hidden relative">
                  <div className="relative overflow-hidden flex items-center justify-center aspect-[0.71/1] w-full">
                    <div className='overflow-hidden flex items-center justify-center w-full h-[calc(100%-2px)]'>
                      <div className='relative w-full h-[130%]'>
                        <Image
                          src={item.image.url}
                          alt={item.image.alt} 
                          fill
                          sizes="400px"
                          className="w-auto object-cover"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-dusty-pink-light card-polygon-overlay scale-[1.01] absolute inset-0"></div>
                  <div className='bg-dusty-pink-light group-hover:opacity-100 opacity-0 transition-opacity duration-500 absolute inset-0 '></div>

                  <div className="z-10 absolute group-hover:opacity-100 opacity-0 transition-opacity duration-500 inset-0 flex w-full items-start flex-col justify-between p-[2rem] lg:py-[3.2rem]">
                    <p  className="text-link mb-auto">
                      {btn_text}
                    </p>
                    <div>
                      <div className="w-full flex flex-col gap-[1.6rem]">
                        {item.text.map((t, textIndex) => (
                          <p key={`${index}-overlay-text-${textIndex}`} className="text-1">
                            {t.text}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </a>
                <h3 className="mb-[1.6rem] heading-4">{item.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>

    </section>
  );
};

export default HomeWWD;
